import { appColors, BackrsCard, ContentNugget } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { NuggetList } from '@backrs_ui/grow-ui/src/components/Nuggets/NuggetList';

import { Box, Typography, Button, SxProps, Theme } from '@mui/material';

import { ProficiencyLevelWidgetIcon } from '../atoms/ProficiencyLevelWidgetIcon';

const PROFICIENCY_STRINGS = appStrings.modules.competency.proficiency;
const COMPETENCY_STRINGS = appStrings.modules.competency;

type ProficiencyLevelWidgetProps = Readonly<{
  currentProficiencyLevel: number;
  setOpenProficiencyDialog: (open: boolean) => void;
  nuggets: ContentNugget[];
  onNuggetClick: (nugget: ContentNugget) => void;
  completedNuggetIds?: number[];
}>;

export const ProficiencyLevelWidget = ({
  currentProficiencyLevel,
  setOpenProficiencyDialog,
  nuggets,
  onNuggetClick,
  completedNuggetIds,
}: ProficiencyLevelWidgetProps) => {
  return (
    <Box sx={cardStyling}>
      <BackrsCard sx={{ backgroundColor: 'transparent' }}>
        <Box sx={widgetContainerStyle}>
          <Typography variant="h5" sx={smallScreenTitleStyle}>
            {PROFICIENCY_STRINGS.widgetTitle}
          </Typography>
          <ProficiencyLevelWidgetIcon currentProficiencyLevel={currentProficiencyLevel} />

          <Box sx={titleAndButtonContainerStyle}>
            <Typography variant="h5" sx={largeScreenTitleStyle}>
              {PROFICIENCY_STRINGS.widgetTitle}
            </Typography>
            <Button
              variant="contained"
              onClick={() => setOpenProficiencyDialog(true)}
              sx={buttonStyle}
            >
              {PROFICIENCY_STRINGS.widgetButtonLabel}
            </Button>
          </Box>
        </Box>
        <Box sx={nuggetContainerStyle}>
          {nuggets.length ? (
            <NuggetList
              columnHeader={COMPETENCY_STRINGS.columnHeaders.growByDoing}
              nuggets={nuggets}
              completedNuggetIds={completedNuggetIds}
              onClick={onNuggetClick}
            />
          ) : (
            <></>
          )}
        </Box>
      </BackrsCard>
    </Box>
  );
};

const TOP_BAR_HEIGHT = 76;
const TOP_BAR_BOTTOM_MARGIN = 16;

const nuggetContainerStyle: SxProps<Theme> = {
  '@media (max-width: 837px)': {
    position: 'absolute !important',
    top: '-9999px !important',
    left: '-9999px !important',
  },
};

const cardStyling: SxProps<Theme> = {
  top: TOP_BAR_HEIGHT + TOP_BAR_BOTTOM_MARGIN,
  height: '100%',
  zIndex: 1,
  right: 0,
  flexDirection: 'column',
  display: 'flex',
  backgroundColor: appColors.color.blue5,
  fontFamily: 'Sarabun',
  paddingLeft: 4,
  paddingRight: 4,
} as SxProps<Theme>;

const titleAndButtonContainerStyle: SxProps<Theme> = {
  ml: 4,
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  '@media (max-width: 837px)': {
    ml: 0,
    mt: 2,
  },
};

const largeScreenTitleStyle: SxProps<Theme> = {
  mb: 3,
  color: appColors.color.blue1,
  fontSize: '28px',
  lineHeight: '28px',
  fontWeight: 700,
  fontFamily: 'Sarabun',
  '@media (max-width: 837px)': {
    display: 'none',
  },
};

const smallScreenTitleStyle: SxProps<Theme> = {
  mb: 3,
  color: appColors.color.blue1,
  fontSize: '28px',
  lineHeight: '28px',
  fontWeight: 700,
  fontFamily: 'Sarabun',
  alignSelf: 'center',
  '@media (min-width: 837px)': {
    display: 'none',
  },
};

const buttonStyle: SxProps<Theme> = {
  backgroundColor: `${appColors.color.blue1}21`,
  color: appColors.neutral.gray1,
  fontWeight: 700,
  fontFamily: 'Sarabun',
  fontSize: '18px',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: `${appColors.color.blue1}21`,
  },
  textTransform: 'none',
  px: 3,
};

const widgetContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  mb: 2,
  minHeight: '128px',
  '@media (max-width: 837px)': {
    mb: 0,
    minHeight: 0,
    flexDirection: 'column',
  },
};
